import React, { useState, useRef, useEffect } from "react";
import "./TravelerRoom.css";
import { MenuItem, Select, FormControl, InputLabel, Box } from "@mui/material";
import minus from "../../assets/images/packages/svg/minus.svg";
import plus from "../../assets/images/packages/svg/plus.svg";
import { useTranslation } from "../../contexts/TranslationContext";
import { FaUser } from "react-icons/fa";
import Cookies from "js-cookie"

const MAX_ROOMS = 6;
const MAX_ADULTS_PER_ROOM = 6;
const MAX_CHILDREN_PER_ROOM = 4;
const CHILD_MIN_AGE = 0;
const CHILD_MAX_AGE = 18;

const TravelerRoom = ({ rooms, onRoomsChange ={}, isFrom="", className = "" }) => {
  const Lang = Cookies.get("preferred_language") || "en"; 
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const { t } = useTranslation();
  const isHotelList = isFrom === "hotelList";
  const childAgeMenuProps = {
    disablePortal: false,
    disableScrollLock: true,
    PaperProps: {
      className: "child-age-menu-paper",
    },
  };

  // Toggle dropdown
  const handleOpen = () => setOpen(!open);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target) && 
        !document.querySelector(".MuiPopover-root")?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle room adults change
  const handleRoomAdultsChange = (roomIndex, change) => {
    const updatedRooms = [...rooms];
    updatedRooms[roomIndex].Adults = Math.min(
      MAX_ADULTS_PER_ROOM,
      Math.max(1, updatedRooms[roomIndex].Adults + change)
    );
    onRoomsChange(updatedRooms);
  };

  // Handle room children change
  const handleRoomChildrenChange = (roomIndex, change) => {
    const updatedRooms = [...rooms];
    const newChildren = Math.min(
      MAX_CHILDREN_PER_ROOM,
      Math.max(0, updatedRooms[roomIndex].Children + change)
    );
    
    updatedRooms[roomIndex].Children = newChildren;
    
    // Update children ages array
    updatedRooms[roomIndex].ChildrenAges = newChildren > updatedRooms[roomIndex].ChildrenAges.length
      ? [...updatedRooms[roomIndex].ChildrenAges, null]
      : updatedRooms[roomIndex].ChildrenAges.slice(0, newChildren);
    
    onRoomsChange(updatedRooms);
  };

  // Handle child age change
  const handleChildAgeChange = (roomIndex, childIndex, age) => {
    const updatedRooms = [...rooms];
    updatedRooms[roomIndex].ChildrenAges[childIndex] = age;
    onRoomsChange(updatedRooms);
  };

  // Add new room
  const handleAddRoom = () => {
    if (rooms.length >= MAX_ROOMS) return;

    onRoomsChange([
      ...rooms,
      { Adults: 1, Children: 0, ChildrenAges: [] }
    ]);
  };

  // Remove room
  const handleRemoveRoom = (index) => {
    if (rooms.length > 1) {
      const updatedRooms = rooms.filter((_, i) => i !== index);
      onRoomsChange(updatedRooms);
    }
  };

  // Validate and close
  const handleClose = () => {
    const hasErrors = rooms.some(room => 
      room.Children > 0 && room.ChildrenAges.some(age => age === null || age === "")
    );

    if (hasErrors) {
      setError(true);
    } else {
      setError(false);
      setOpen(false);
    }
  };

  // Calculate totals
  const totalAdults = rooms.reduce((sum, room) => sum + room.Adults, 0);
  const totalChildren = rooms.reduce((sum, room) => sum + room.Children, 0);

  return (
    <div className={`traveler-container ${className}`.trim()} ref={containerRef}>
      {/* Input Field */}
      <div className={`traveler-input ${isHotelList ? "search-box" : ""}`} onClick={handleOpen}>
       {
          isHotelList ? (
            <div className="hotellist-traveler-value">
                <FaUser className="hotellist-icon" />
                <div className="search-texts">
                  <div className="search-label">{t("Travelers")}</div>
                  <div className="search-value">
                   {
                      Lang === "en"
                        ? `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}, ${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}, ${rooms.length} Room${rooms.length > 1 ? 's' : ''}`
                        : `${totalAdults} ${totalAdults > 1 ? 'بالغين' : 'بالغ'}, ${totalChildren} ${totalChildren > 1 ? 'أطفال' : 'طفل'}, ${rooms.length} ${rooms.length > 1 ? 'غرف' : 'غرفة'}`
                    }
                  </div>
                </div>
            </div>
          ) : (
            <>
              {totalAdults + totalChildren} {t("Travelers")}, {rooms.length} {t("Room")}
              {rooms.length > 1 ? 's' : ''}
            </>
          )
        } 
       
      </div>

      {/* Dropdown */}
      {open && (
        <div className={`traveler-dropdown ${isHotelList ? "hotellist-traveler-dropdown" : ""}`}>
          {rooms.map((room, roomIndex) => (
            <div key={roomIndex} className="room-section">
              <div className="traveler-room-header  mb-3">
                <h4 className="traveler-room-title">{t("Room")} {roomIndex + 1}</h4>
              
              </div>

              <div className="mb-3">
                <div className="traveler-counter-container">
                  <span className="traveler-name">{t("Adults")}</span>
                  <div className="traveler-counter">
                    <button 
                      type="button" 
                      className="traveler-counter-btn" 
                      onClick={() => handleRoomAdultsChange(roomIndex, -1)}
                      disabled={room.Adults <= 1}
                    >
                      <img src={minus} alt="minus" />
                    </button>
                    <span>{room.Adults}</span>
                    <button 
                      type="button" 
                      className="traveler-counter-btn" 
                      onClick={() => handleRoomAdultsChange(roomIndex, 1)}
                      disabled={room.Adults >= MAX_ADULTS_PER_ROOM}
                    >
                      <img src={plus} alt="plus" />
                    </button>
                  </div>
                </div>

                <div className="traveler-counter-container">
                  <div className="traveler-name">
                    <span>{t("Children")}</span>
                    <span className="traveler-name-desc">
                      {t("Ages")} {CHILD_MIN_AGE} {t("To")} {CHILD_MAX_AGE}
                    </span>
                  </div>
                  <div className="traveler-counter">
                    <button 
                      type="button" 
                      className="traveler-counter-btn" 
                      onClick={() => handleRoomChildrenChange(roomIndex, -1)}
                      disabled={room.Children <= 0}
                    >
                      <img src={minus} alt="minus" />
                    </button>
                    <span>{room.Children}</span>
                    <button 
                      type="button" 
                      className="traveler-counter-btn" 
                      onClick={() => handleRoomChildrenChange(roomIndex, 1)}
                      disabled={room.Children >= MAX_CHILDREN_PER_ROOM}
                    >
                      <img src={plus} alt="plus" />
                    </button>
                  </div>
                </div>

                {/* Child Age Dropdowns */}
                {room.Children > 0 && (
                  <div className="age-selectors">
                    {room.ChildrenAges.map((age, childIndex) => (
                      <Box key={childIndex} sx={{ marginTop: "15px" }}>
                        <FormControl fullWidth error={error && (age === null || age === "")}>
                          <InputLabel className="child-select-label">
                          {t("Child")} {childIndex + 1} {t("Age")} <span>*</span>
                          </InputLabel>
                          <Select
                            value={age ?? ""}
                            onChange={(e) => handleChildAgeChange(roomIndex, childIndex, e.target.value)}
                            MenuProps={childAgeMenuProps}
                            required
                            label={t("ChildAge")}
                            sx={{
                              "& .MuiSelect-select": {
                                padding: "12px",
                                fontSize: "14px",
                                fontFamily: "Dmsans300"
                              }
                            }}
                          >
                            {[...Array(CHILD_MAX_AGE - CHILD_MIN_AGE + 1).keys()].map((num) => {
                              const ageValue = CHILD_MIN_AGE + num;

                              return (
                              <MenuItem key={ageValue} value={ageValue}>
                                {ageValue}
                              </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                  </div>
                )}
              </div>
              {rooms.length > 1 && (
                <div className="room-btn-container mb-4">
                    <button 
                        type="button" 
                        className="room-btn"
                        onClick={() => handleRemoveRoom(roomIndex)}
                    >
                       {t("RemoveRoom")} 
                    </button>
                </div>
                
                )}
            </div>
          ))}

          <div className="room-btn-container mb-3"> 
            <button 
                type="button" 
                className="room-btn"
                onClick={handleAddRoom}
                disabled={rooms.length >= MAX_ROOMS}
            >
              {t("AddAnotherRoom")} 
                
            </button>
          </div>
        

          {error && <p className="traveler-error-msg">
            {t("childrenAgeNote")} 
            </p>}

          <button type="button" className="close-btn" onClick={handleClose}>
          {t("Done")} 
            
          </button>
        </div>
      )}
    </div>
  );
};

export default TravelerRoom;
